/**
 * The web address ("slug") derived for an organization or workspace, shared by
 * `workspace/NewWorkspaceDialog.tsx`, `workspace/FounderBootstrapDialog.tsx` and
 * `onboarding/OnboardingFlow.tsx` (crossmarc/steward-app#2050).
 *
 * appcore accepts 2 to 64 characters of `[a-z0-9-]` for a tenant or workspace slug
 * (`platform-api/handlers/onboarding.ts` line 12 and `tenants.ts`, read 2026-10-05). Its pattern
 * does not forbid a leading or trailing hyphen, but the derived slug never has one.
 * A name may be 200 characters, so the derived slug is cut to 64, and the hyphen strip runs AFTER
 * the cut so the cut cannot leave a trailing hyphen. The name itself is never truncated.
 */
export const SLUG_MIN = 2;
export const SLUG_MAX = 64;

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, SLUG_MAX)
    .replace(/-+$/, '');
}

/** A typed slug cut to 64, so no path (a paste, a programmatic value) can send more than appcore accepts. */
export function clampSlug(slug: string): string {
  return slug.slice(0, SLUG_MAX);
}

/**
 * True when a slug is too short to send. `active` is whether the user has given the slug
 * something to be judged on (typed a name, or edited the slug field), so an untouched empty form
 * does not open with an error.
 */
export function slugTooShort(slug: string, active: boolean): boolean {
  return active && slug.length < SLUG_MIN;
}

export const SLUG_TOO_SHORT_MESSAGE =
  'A web address needs at least 2 characters, using the letters a to z and the numbers 0 to 9. Please type one here.';
